import { Button, Card, Checkbox, Group, Radio, Text, Textarea, Tooltip } from '@mantine/core'
import React, { useEffect, useState } from 'react'
import {
  Category,
  CategoryPreview,
  HackathonPreview,
  Idea,
  IdeaFormType,
  Skill,
  SkillPreview,
} from '../../common/types'
import { getListOfSkills, getSkillDetails } from '../../actions/SkillActions'
import { getCategoryDetails, getListOfCategories } from '../../actions/CategoryActions'
import { showNotification, updateNotification } from '@mantine/notifications'
import { createIdea, editIdea } from '../../actions/IdeaActions'
import { styles } from '../../common/styles'
import { useMsal } from '@azure/msal-react'
import { dark2, JOIN_BUTTON_COLOR } from '../../common/colors'
import { CustomCheckIcon, CustomXIcon } from '../NotificationIcons'

type IProps = {
  hackathon: HackathonPreview
  ownerId?: string
  context: string
  ideaId: string | null
  setOpened?: (boolean: boolean) => void
  idea?: Idea
  onSuccess: () => void
}

function IdeaForm(props: IProps) {
  const { instance } = useMsal()
  const { hackathon, ownerId, context, ideaId, setOpened, idea, onSuccess } =
    props
  const { classes } = styles()
  const [isLoading, setIsLoading] = useState(true)
  const [buttonIsDisabled, setButtonIsDisabled] = useState(true)
  const [availableSkills, setAvailableSkills] = useState({
    skills: [] as SkillPreview[],
  })
  const [skills, setSkills] = useState<string[]>(
    idea?.requiredSkills?.map((skill) => skill.id) || []
  )
  const [skillData, setSkillData] = useState([] as Skill[])
  const [availableCategories, setAvailableCategories] = useState({
    categories: [] as CategoryPreview[],
  })
  const [category, setCategory] = useState<string>(idea?.category?.id || '')
  const [categoryData, setCategoryData] = useState([] as Category[])
  const [ideaText, setIdeaText] = useState({
    ownerId: ownerId || '',
    hackathonId: hackathon.id,
    title: '',
    description: '',
    problem: '',
    goal: '',
    creationDate: new Date(),
  })
  const maxIdeaTitleLength = 100

  const setIdea = () => {
    if (idea) {
      setIdeaText({
        ...ideaText,
        title: idea.title,
        description: idea.description,
        problem: idea.problem,
        goal: idea.goal,
      })
    }
  }

  const ideaSkill = idea?.requiredSkills?.map((skill) => skill.id)

  const loadAvailableSkills = () => {
    getListOfSkills(instance).then((data) => {
      setAvailableSkills({
        ...availableSkills,
        skills: data?.skills || [],
      })
    })
  }

  const loadSkillDetails = () => {
    Promise.all(
        availableSkills.skills.map((skill) =>
            getSkillDetails(instance, skill.id)
        )).then((skillsData) => {
      setSkillData(skillsData)
    })
  }

  const getSkillDescription = (id: string) => {
    return skillData.find((skill) => skill.id === id)?.description ?? null
  }

  const skillsList = availableSkills.skills.map((skill) => (
    <Checkbox value={skill.id} label={
      <Tooltip
          multiline
          width={220}
          transition='fade'
          transitionDuration={200}
          color='gray'
          label={getSkillDescription(skill.id)}
      >
        <Text>
          {skill.name}
        </Text>
      </Tooltip>
    } key={skill.id} />
  ))

  const loadAvailableCategories = () => {
    getListOfCategories(instance, hackathon.id).then((data) => {
      setAvailableCategories(prevState => ({
        ...prevState,
        categories: data?.categories || [],
      }))
      setIsLoading(false)
    })
  }

  const loadCategoryDetails = () => {
    Promise.all(
        availableCategories.categories.map((category) =>
            getCategoryDetails(instance, category.id)
        )
    ).then((categoriesData) => {
      setCategoryData(categoriesData)
    })
  }

  const getCategoryDescription = (id: string) => {
    return categoryData.find((category) => category.id === id)?.description ?? null
  }

  const categoriesList = availableCategories?.categories?.map((category) => (
    <Radio value={category.id} label={
      <Tooltip
          multiline
          width={220}
          transition='fade'
          transitionDuration={200}
          color='gray'
          label={getCategoryDescription(category.id)}
      >
        <Text>
          {category.title}
        </Text>
      </Tooltip>
    } key={category.id} />
  ))

  function handleChange(event: React.ChangeEvent<HTMLTextAreaElement>) {
    setIdeaText((prevIdeaText) => ({
      ...prevIdeaText,
      hackathonId: hackathon.id,
      [event.target.name]: event.target.value,
    }))
  }

  async function createThisIdea(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    setButtonIsDisabled(true)
    showNotification({
      id: 'idea-load',
      loading: true,
      title: `Creating "${ideaText.title}"`,
      message: undefined,
      autoClose: false,
      // disallowClose: true,
    })
    createIdea(instance, ideaText, skills, [category]).then(
      async (response) => {
        setButtonIsDisabled(false)
        setCategory('')
        setSkills([])
        setIdeaText((prevState) => ({
          ...prevState,
          title: '',
          description: '',
          problem: '',
          goal: '',
        }))
        if (JSON.stringify(response).toString().includes('error')) {
          updateNotification({
            id: 'idea-load',
            color: 'red',
            title: 'Failed to create idea',
            message: undefined,
            icon: <CustomXIcon />,
            autoClose: 5000,
          })
        } else {
          updateNotification({
            id: 'idea-load',
            color: 'teal',
            title: `Created "${ideaText.title}"`,
            message: undefined,
            icon: <CustomCheckIcon />,
            autoClose: 5000,
          })
          onSuccess()
        }
      }
    )
  }

  function editThisIdea(event: React.MouseEvent<HTMLButtonElement>) {
    event.preventDefault()
    setButtonIsDisabled(true)
    showNotification({
      id: 'idea-load',
      loading: true,
      title: `Editing "${ideaText.title}"`,
      message: undefined,
      autoClose: false,
      // disallowClose: true,
    })
    editIdea(instance, ideaId!, ideaText, skills, [category]).then(
      (response) => {
        setButtonIsDisabled(false)
        if (setOpened) {
          setOpened(false)
        }
        if (JSON.stringify(response).toString().includes('error')) {
          updateNotification({
            id: 'idea-load',
            color: 'red',
            title: 'Failed to Edit idea',
            message: undefined,
            icon: <CustomXIcon />,
            autoClose: 5000,
          })
        } else {
          updateNotification({
            id: 'idea-load',
            color: 'teal',
            title: `Edited "${ideaText.title}"`,
            message: undefined,
            icon: <CustomCheckIcon />,
            autoClose: 5000,
          })
          onSuccess()
        }
      }
    )
  }

  useEffect(() => {
    loadAvailableSkills()
    loadAvailableCategories()
    setIdea()
    setCategory('')
    setSkills([])
  }, [])

  useEffect(() => {
    setCategory(availableCategories.categories[0]?.id || '')
    setSkills([])
    loadSkillDetails()
    loadCategoryDetails()
  }, [availableCategories])

  useEffect(() => {
    if (
      ideaText.description.length > 1 &&
      ideaText.title.length <= maxIdeaTitleLength &&
      ideaText.title.length > 1
    ) {
      setButtonIsDisabled(false)
    } else {
      setButtonIsDisabled(true)
    }
  }, [ideaText, category, skills])

  useEffect(() => {
    if (ownerId) {
      setIdeaText((prevIdeaText) => ({
        ...prevIdeaText,
        ownerId: ownerId.toString(),
      }))
    }
  }, [ownerId])

  useEffect(() => {
    loadAvailableCategories()
  }, [hackathon])

  return (
    <>
      <Card withBorder className={classes.card}>
        {isLoading && <div>Loading relevant data...</div>}
        {!isLoading && (
          <div>
            <Card.Section className={classes.borderSection}>
              <Textarea
                label='Title'
                required
                error={
                  ideaText.title.length > maxIdeaTitleLength
                    ? 'max ' + maxIdeaTitleLength + ' chars'
                    : false
                }
                placeholder='Title'
                maxRows={1}
                autosize
                onChange={handleChange}
                name='title'
                value={ideaText.title}
                className={classes.label}
              />
            </Card.Section>
            <Card.Section className={classes.borderSection}>
              <Textarea
                label='Description'
                required
                placeholder='Description'
                minRows={2}
                maxRows={3}
                autosize
                onChange={handleChange}
                name='description'
                value={ideaText.description}
                className={classes.label}
              />
            </Card.Section>
            <Card.Section className={classes.borderSection}>
              <Textarea
                label='Problem'
                placeholder='which problelm does it solve (optional)'
                minRows={2}
                maxRows={3}
                autosize
                onChange={handleChange}
                name='problem'
                value={ideaText.problem}
                className={classes.label}
              />
            </Card.Section>
            <Card.Section className={classes.borderSection}>
              <Textarea
                label='Goal'
                placeholder='the goal for the hackweek is... (optional)'
                minRows={2}
                maxRows={3}
                autosize
                onChange={handleChange}
                name='goal'
                value={ideaText.goal}
                className={classes.label}
              />
            </Card.Section>

            <Card.Section className={classes.borderSection}>
              <Checkbox.Group
                label='Skills'
                description='Choose one or more skills that could help to bring your idea to life'
                onChange={setSkills}
                defaultValue={ideaSkill}
                className={classes.label}
              >
                {skillsList}
              </Checkbox.Group>
            </Card.Section>

            <Card.Section className={classes.noBorderSection}>
              <Radio.Group
                label='Category'
                description='Choose one hackathon category for your idea'
                onChange={setCategory}
                required
                defaultValue={idea?.category?.id}
                value={category}
                className={classes.label}
              >
                {categoriesList}
              </Radio.Group>
            </Card.Section>
          </div>
        )}
      </Card>
      <Group position='right' mt='xl'>
        {context === IdeaFormType.Edit && (
          <Button
            style={{
              backgroundColor: !buttonIsDisabled ? JOIN_BUTTON_COLOR : dark2,
            }}
            disabled={buttonIsDisabled}
            onClick={editThisIdea}
          >
            Save
          </Button>
        )}
        {context === IdeaFormType.New && (
          <Button
            style={{
              backgroundColor: !buttonIsDisabled ? JOIN_BUTTON_COLOR : dark2,
            }}
            disabled={buttonIsDisabled}
            onClick={createThisIdea}
          >
            Create
          </Button>
        )}
      </Group>
    </>
  )
}

export default IdeaForm
